import type { MouseEvent, PointerEvent, ReactNode } from 'react';
import type { SpawnControlKind, SpawnControlRowProps } from './types';

const KIND_META: Record<
  SpawnControlKind,
  {
    countRoot: string;
    actionRoot: string;
    moreLabel: string;
    fewerLabel: string;
    activeLabel: string;
    idleLabel: string;
  }
> = {
  enemy: {
    countRoot: 'arcade-enemy-count',
    actionRoot: 'arcade-spawn-enemy',
    moreLabel: 'More enemies',
    fewerLabel: 'Fewer enemies',
    activeLabel: 'ENEMY ACTIVE',
    idleLabel: 'SPAWN ENEMY',
  },
  teammate: {
    countRoot: 'arcade-teammate-count',
    actionRoot: 'arcade-spawn-teammate',
    moreLabel: 'More teammates',
    fewerLabel: 'Fewer teammates',
    activeLabel: 'ALLY ACTIVE',
    idleLabel: 'SPAWN TEAMMATE',
  },
};

function joinClass(...parts: Array<string | undefined | false>): string {
  return parts.filter(Boolean).join(' ');
}

function stop(event: MouseEvent | PointerEvent) {
  event.stopPropagation();
}

/**
 * Shared count stepper + SPAWN action used by the in-run HUD and Settings → Game.
 * Preserves existing BEM class names so ArcadeHud.css / settings styles still apply.
 */
export function SpawnControlRow({
  kind,
  rowClassName,
  count,
  max,
  unitAlive,
  onCountChange,
  onSpawn,
  isolatePointer = false,
  countClassName,
  actionClassName,
}: SpawnControlRowProps): ReactNode {
  const meta = KIND_META[kind];
  const isolate = isolatePointer
    ? {
        onClick: stop,
        onMouseDown: stop,
        onPointerDown: stop,
      }
    : undefined;

  const onMore = (event: MouseEvent<HTMLButtonElement>) => {
    if (isolatePointer) stop(event);
    onCountChange(Math.min(max, count + 1));
  };
  const onFewer = (event: MouseEvent<HTMLButtonElement>) => {
    if (isolatePointer) stop(event);
    onCountChange(Math.max(1, count - 1));
  };
  const onAction = (event: MouseEvent<HTMLButtonElement>) => {
    if (isolatePointer) stop(event);
    onSpawn();
  };

  return (
    <div className={rowClassName}>
      <div className={joinClass(meta.countRoot, countClassName)} {...isolate}>
        <button
          type="button"
          className={`${meta.countRoot}__btn`}
          disabled={unitAlive || count >= max}
          aria-label={meta.moreLabel}
          onClick={onMore}
        >
          ▲
        </button>
        <div className={`${meta.countRoot}__display`}>
          <span className={`${meta.countRoot}__value`} aria-live="polite">
            {count}
          </span>
        </div>
        <button
          type="button"
          className={`${meta.countRoot}__btn`}
          disabled={unitAlive || count <= 1}
          aria-label={meta.fewerLabel}
          onClick={onFewer}
        >
          ▼
        </button>
      </div>
      <button
        type="button"
        className={joinClass(meta.actionRoot, actionClassName)}
        disabled={unitAlive}
        onClick={onAction}
      >
        {unitAlive ? meta.activeLabel : meta.idleLabel}
      </button>
    </div>
  );
}
